import i18n from 'i18next'
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'

import { getEnvUrl } from '@/api/utils'

const PRESCRIPTION_SURVEYS_NAMESPACE = 'prescriptionSurveys'
const BASE_NAMESPACE = 'base'
const TRANSLATIONS_API_URL = getEnvUrl('VITE_TRANSLATIONS_API_URL')

const NAMESPACES = [BASE_NAMESPACE, PRESCRIPTION_SURVEYS_NAMESPACE]

type TranslationNamespace = (typeof NAMESPACES)[number]
type TranslationResources = Record<string, string>
type TranslationsApiResponse = Partial<Record<TranslationNamespace, TranslationResources>>

const translationRequests = new Map<string, Promise<TranslationsApiResponse>>()

const normalizeTranslationLanguage = (language: string) => {
  const [languageCode, regionCode] = language.replace('_', '-').split('-')
  return regionCode ? `${languageCode}-${regionCode.toUpperCase()}` : languageCode
}

const loadTranslations = async (language: string) => {
  const normalizedLanguage = normalizeTranslationLanguage(language)
  const cachedRequest = translationRequests.get(normalizedLanguage)
  if (cachedRequest) return cachedRequest

  const request = fetch(`${TRANSLATIONS_API_URL}/translations/${encodeURIComponent(normalizedLanguage)}`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to load translations for ${normalizedLanguage}: ${response.status} ${response.statusText}`)
      }

      return (await response.json()) as TranslationsApiResponse
    })
    .catch((error) => {
      translationRequests.delete(normalizedLanguage)
      throw error
    })

  translationRequests.set(normalizedLanguage, request)
  return request
}

const loadTranslationNamespace = async (language: string, namespace: string) =>
  (await loadTranslations(language))[namespace as TranslationNamespace] ?? {}

i18n
  .use(I18nextBrowserLanguageDetector)
  .use(
    resourcesToBackend((language, namespace, callback) => {
      loadTranslationNamespace(language, namespace)
        .then((resources) => callback(null, resources))
        .catch((error) => callback(error, null))
    })
  )
  .use(initReactI18next)
  .init({
    ns: NAMESPACES,
    defaultNS: BASE_NAMESPACE,
    fallbackNS: PRESCRIPTION_SURVEYS_NAMESPACE,
    fallbackLng: 'en',
    detection: {
      order: ['querystring', 'navigator'],
      lookupQuerystring: 'language'
    },
    lowerCaseLng: true
  })

export default i18n
